import styles from "./Modals.module.scss";
import React, { useEffect, useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import omit from "lodash/omit";
import { AWS_REGION_LIST } from "@whatslly/commons/utils";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { Switch } from "@whatslly/ui-components/v2/BaseElements/Switch";
import { QuestionMarkIcon } from "@whatslly/ui-components/QuestionMarkIcon";
import { Tooltip } from "@whatslly/ui-components/Tooltip";

const LANG_OPTIONS = [
  {
    label: "Chinese, CN (zh-CN)",
    value: "zh-CN",
  },
  {
    label: "English, AU (en-AU)",
    value: "en-AU",
  },
  {
    label: "English, UK (en-GB)",
    value: "en-GB",
  },
  {
    label: "English, US (en-US)",
    value: "en-US",
  },
  {
    label: "French, CA (fr-CA)",
    value: "fr-CA",
  },
  {
    label: "French, FR (fr-FR)",
    value: "fr-FR",
  },
  {
    label: "German, DE (de-DE)",
    value: "de-DE",
  },
  {
    label: "Hindi, IN (hi-IN)",
    value: "hi-IN",
  },
  {
    label: "Italian, IT (it-IT)",
    value: "it-IT",
  },
  {
    label: "Japanese, JP (ja-JP)",
    value: "ja-JP",
  },
  {
    label: "Korean, KR (ko-KR)",
    value: "ko-KR",
  },
  {
    label: "Portuguese, BR (pt-BR)",
    value: "pt-BR",
  },
  {
    label: "Spanish, US (es-US)",
    value: "es-US",
  },
  {
    label: "Thai, TH (th-TH)",
    value: "th-TH",
  },
];

type AWSRegionsType = (typeof AWS_REGION_LIST)[number];

const BASE_DATA: Destination = {
  type: "amazonTranscribe" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.AUDIO_TRANSCRIBES,
  settings: {
    region: "us-east-2",
    accessKeyId: "",
    secretAccessKey: "",
    option: {
      type: "specificLanguage",
      languageCode: "en-US",
    },
    allowLongAudioTranscription: false,
    enableVideoTranscription: false,
  },
};
type FormDataType = FormDestinationDataType<"amazonTranscribe">;
type AmazonTranscribeFormData = FormDataType & {
  allowLongAudioTranscription?: boolean;
  temporaryS3Bucket?: string;
  temporaryS3Region?: string;
  temporaryS3Prefix?: string;
  enableVideoTranscription?: boolean;
};

const getDefaultLangOpt = (settings: FormDataType | undefined) => {
  return settings?.option?.type === "specificLanguage"
    ? LANG_OPTIONS.find((opt) => settings.option.type === "specificLanguage" && opt.value === settings.option.languageCode)
    : LANG_OPTIONS.find((opt) => opt.value === "en-US");
};

export const AmazonTranscribeModalForm: React.FC<DestinationFormProps<"amazonTranscribe">> = ({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<AmazonTranscribeFormData>>(null);
  const [regionValue, setRegionValue] = useState<AWSRegionsType>(AWS_REGION_LIST.find((opt) => opt === settings?.region) ?? AWS_REGION_LIST[0]);
  const [temporaryS3RegionValue, setTemporaryS3RegionValue] = useState<AWSRegionsType>(
    AWS_REGION_LIST.find((opt) => settings?.allowLongAudioTranscription && opt === settings.temporaryS3Region) ?? AWS_REGION_LIST[0]
  );
  const [langOptValue, setLangOptValue] = useState<string>(getDefaultLangOpt(settings)?.value ?? "");
  const [allowLongAudioTranscriptionValue, setAllowLongAudioTranscriptionValue] = useState<boolean>(Boolean(settings?.allowLongAudioTranscription));
  const [enableVideoTranscriptionValue, setEnableVideoTranscriptionValue] = useState<boolean>(Boolean(settings?.enableVideoTranscription));
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const validate = async (values?: AmazonTranscribeFormData) => {
    const errors: FormikErrors<PartialRecord<keyof AmazonTranscribeFormData, string>> = {};
    if (!values?.region) errors.region = t("field_is_required");
    if (!values?.option) errors.option = t("field_is_required");
    if (!values?.accessKeyId) errors.accessKeyId = t("field_is_required");
    if (!values?.secretAccessKey) errors.secretAccessKey = t("field_is_required");
    if (allowLongAudioTranscriptionValue && !values?.temporaryS3Bucket) errors.temporaryS3Bucket = t("field_is_required");
    if (allowLongAudioTranscriptionValue && !temporaryS3RegionValue) errors.temporaryS3Region = t("field_is_required");

    Object.keys(errors).length > 0 ? setFormikErrorHasErrors(true) : setFormikErrorHasErrors(false);
    return errors;
  };

  const getFormValues = () => {
    const data: Partial<AmazonTranscribeFormData> = formikContext.current?.values ?? {};
    const baseSettings = {
      ...omit(data, ["languageCode", "allowLongAudioTranscription", "temporaryS3Bucket", "temporaryS3Region", "temporaryS3Prefix", "enableVideoTranscription"]),
      region: regionValue,
      option: {
        type: "specificLanguage" as const,
        languageCode: langOptValue,
      },
      enableVideoTranscription: enableVideoTranscriptionValue,
    };

    const transcribeSettings = allowLongAudioTranscriptionValue
      ? {
          ...baseSettings,
          allowLongAudioTranscription: true as const,
          temporaryS3Bucket: data.temporaryS3Bucket ?? "",
          temporaryS3Region: temporaryS3RegionValue,
          temporaryS3Prefix: data.temporaryS3Prefix,
        }
      : {
          ...baseSettings,
          allowLongAudioTranscription: false as const,
        };

    return {
      ...BASE_DATA,
      settings: transcribeSettings,
    };
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, [allowLongAudioTranscriptionValue, temporaryS3RegionValue]);

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  return (
    <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.language")} required />
        <FormSelect<{ label: string; value: string }>
          ignoreMenuDirection={true}
          isSearchable={true}
          name={"option"}
          paddingInline="none"
          options={LANG_OPTIONS}
          onChange={(selectedOption: (typeof LANG_OPTIONS)[number]) => setLangOptValue(selectedOption?.value)}
          defaultValue={getDefaultLangOpt(settings)}
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.region")} required />
        <FormSelect<{ label: AWSRegionsType; value: AWSRegionsType }>
          ignoreMenuDirection={true}
          isSearchable={true}
          name={"region"}
          paddingInline="none"
          onChange={(selectedOption: { label: AWSRegionsType; value: AWSRegionsType }) => setRegionValue(selectedOption?.value)}
          options={AWS_REGION_LIST.map((opt) => ({ label: opt, value: opt }))}
          defaultValue={
            settings?.region && AWS_REGION_LIST.find((opt) => opt === settings.region)
              ? { label: settings.region as AWSRegionsType, value: settings.region as AWSRegionsType }
              : { label: AWS_REGION_LIST[0], value: AWS_REGION_LIST[0] }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.accesses_key_id")} required />
        <FormTextInput name={"accessKeyId"} paddingInline="none" required />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.secret_access_key")} required />
        <FormPasswordTextInput name={"secretAccessKey"} paddingInline="none" required />
      </div>
      <div className={styles.field}>
        <div className={styles.longTranscribeLabel}>
          <Label className={styles.filedLabel} text={t("destinations_page.enable_video_transcription")} />
          <Tooltip value={t("destinations_page.enable_video_transcription_tooltip")} lineWrap>
            <span>
              <QuestionMarkIcon />
            </span>
          </Tooltip>
        </div>
        <Switch checked={enableVideoTranscriptionValue} onChange={(e) => setEnableVideoTranscriptionValue(e.target.checked)} />
      </div>
      <div className={styles.field}>
        <div className={styles.longTranscribeLabel}>
          <Label className={styles.filedLabel} text={t("destinations_page.enable_long_audio_transcription")} />
          <Tooltip value={t("destinations_page.enable_long_audio_transcription_tooltip")} lineWrap>
            <span>
              <QuestionMarkIcon />
            </span>
          </Tooltip>
        </div>
        <Switch
          checked={allowLongAudioTranscriptionValue}
          onChange={(e) => {
            setAllowLongAudioTranscriptionValue(e.target.checked);
            if (e.target.checked) {
              setFormikErrorHasErrors(true);
            }
          }}
        />
      </div>
      {allowLongAudioTranscriptionValue && (
        <>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.temporary_s3_bucket")} required />
            <FormTextInput name={"temporaryS3Bucket"} paddingInline="none" required />
          </div>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.temporary_s3_region")} required />
            <FormSelect<{ label: AWSRegionsType; value: AWSRegionsType }>
              ignoreMenuDirection={true}
              isSearchable={true}
              name={"temporaryS3Region"}
              paddingInline="none"
              onChange={(selectedOption: { label: AWSRegionsType; value: AWSRegionsType }) => setTemporaryS3RegionValue(selectedOption?.value)}
              options={AWS_REGION_LIST.map((opt) => ({ label: opt, value: opt }))}
              defaultValue={
                settings?.allowLongAudioTranscription &&
                settings.temporaryS3Region &&
                AWS_REGION_LIST.find((opt) => opt === settings.temporaryS3Region)
                  ? { label: settings.temporaryS3Region as AWSRegionsType, value: settings.temporaryS3Region as AWSRegionsType }
                  : { label: AWS_REGION_LIST[0], value: AWS_REGION_LIST[0] }
              }
              isClearable={false}
              required
            />
          </div>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.temporary_s3_prefix")} />
            <FormTextInput name={"temporaryS3Prefix"} paddingInline="none" />
          </div>
        </>
      )}
      <div className={classNames(styles.field, { [styles.hidden]: true })}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_template")} required />
        <FormTextInput name={"filenameTemplate"} paddingInline="none" />
      </div>

      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
};
